import { Fragment } from 'react';
import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import RobotoBold from '../../fonts/Roboto/Roboto-Bold.ttf';
import RobotoRegular from '../../fonts/Roboto/Roboto-Regular.ttf';
import {
  OPERATIONS_NOTES,
  PAYROLL_NOTES,
  STALENESS_NOTE,
  coverageText,
  formatRunAt,
  getFilterLines,
  getOperationsFigures,
  getSections,
  money,
  quantity,
  FINISH_LABELS,
  formatRates,
} from './format';

//The installed react-pdf never applies the built-in Helvetica-Bold: its font fallback skips any font
//named by string, so every glyph lands in plain Helvetica. A registered font is resolved to an
//object first, so bold works.
Font.register({
  family: 'Roboto',
  fonts: [{ src: RobotoRegular }, { src: RobotoBold, fontWeight: 'bold' }],
});

const GRAY = '#666666';
const RULE = '#cccccc';

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 48,
    paddingHorizontal: 40,
    fontFamily: 'Roboto',
    fontSize: 10,
    color: '#000000',
  },
  title: { fontSize: 16, fontWeight: 'bold' },
  muted: { color: GRAY, fontSize: 9, marginTop: 3 },
  section: { marginTop: 18, paddingTop: 12, borderTopWidth: 1, borderTopColor: RULE },
  heading: { fontSize: 12, fontWeight: 'bold' },
  subheading: { color: GRAY, fontSize: 9, marginTop: 2, marginBottom: 8 },
  subtableHeading: {
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    paddingVertical: 4,
  },
  bold: { fontWeight: 'bold' },
  indent: { paddingLeft: 12 },
  //set in from both sides so it reads as a part of labor, not a fifth cost alongside it
  insetLabel: { paddingLeft: 30 },
  insetValue: { paddingRight: 30 },
  label: { flex: 1 },
  value: { width: 140, textAlign: 'right' },
  nameCell: { flex: 1, paddingRight: 6 },
  rateCell: { width: 78, textAlign: 'right' },
  finishRow: { flexDirection: 'row', paddingVertical: 2, color: GRAY, fontSize: 9 },
  numCell: { width: 62, textAlign: 'right' },
  note: { color: GRAY, fontSize: 9, marginTop: 8 },
  notes: { marginTop: 18, paddingTop: 12, borderTopWidth: 1, borderTopColor: RULE },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: GRAY,
    fontSize: 8,
  },
});

const FigureRow = ({ label, value, bold, indent }) => (
  <View style={styles.row} wrap={false}>
    <Text style={[styles.label, bold && styles.bold, indent && styles.indent]}>{label}</Text>
    <Text style={[styles.value, bold && styles.bold]}>{value}</Text>
  </View>
);

const OperationsSection = ({ section }) => {
  const { totals } = section;
  const { costRows, totalCost, margin } = getOperationsFigures(totals, section.taxRates);

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.heading}>{section.heading}</Text>
      <Text style={styles.subheading}>{section.subheading}</Text>

      <FigureRow label='Total cost' value={money(totalCost)} bold />
      {costRows.map(([label, value, share], index) => (
        <Fragment key={label}>
          <FigureRow label={label} value={`${money(value)} (${share.toFixed(1)}%)`} indent />
          {/* the first cost row is labor; runs saved before this figure existed have none */}
          {index === 0 && section.showContractors && totals.contractors !== undefined && (
            <View style={styles.row} wrap={false}>
              <Text style={[styles.label, styles.insetLabel]}>Contractors</Text>
              <Text style={[styles.value, styles.insetValue]}>{money(totals.contractors)}</Text>
            </View>
          )}
        </Fragment>
      ))}
      <FigureRow label='Total sales' value={money(totals.sales)} bold />
      <FigureRow
        label='Gross profit'
        value={`${money(totals.profit)} (${margin.toFixed(1)}%)`}
        bold
      />
      <FigureRow label='Pools finished' value={quantity(totals.poolsFinished)} />
      {section.showYards && <FigureRow label='Yards' value={quantity(totals.yards)} />}
      {section.showBatches && <FigureRow label='Batches' value={quantity(totals.batches)} />}

      <Text style={styles.note}>{coverageText(section.coverage)}</Text>
    </View>
  );
};

//the header row repeats on every page a long table runs onto
const PayeeTable = ({ payees, showPoolColumns }) => {
  const showRate = showPoolColumns && payees.some(payee => payee.finishes);

  return (
    <View>
      <View style={[styles.row, styles.bold]} fixed>
        <Text style={styles.nameCell}>Name</Text>
        <Text style={styles.numCell}>Jobs</Text>
        {showPoolColumns && (
          <>
            <Text style={styles.numCell}>Pools</Text>
            {showRate && <Text style={styles.rateCell}>Rate</Text>}
            <Text style={styles.numCell}>Pool pay</Text>
            <Text style={styles.numCell}>Travel</Text>
            <Text style={styles.numCell}>Extras</Text>
          </>
        )}
        <Text style={styles.numCell}>Pay</Text>
      </View>
      {payees.map(payee => (
        //a person and their finish lines stay together on one page
        <View key={payee.name} style={styles.row} wrap={false}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={styles.nameCell}>{payee.name}</Text>
              <Text style={styles.numCell}>{quantity(payee.jobs)}</Text>
              {showPoolColumns && (
                <>
                  <Text style={styles.numCell}>{quantity(payee.pools)}</Text>
                  {showRate && <Text style={styles.rateCell} />}
                  <Text style={styles.numCell}>{money(payee.poolPay)}</Text>
                  <Text style={styles.numCell}>{money(payee.travel)}</Text>
                  <Text style={styles.numCell}>{money(payee.extras)}</Text>
                </>
              )}
              <Text style={styles.numCell}>{money(payee.pay)}</Text>
            </View>
            {payee.finishes?.map(line => (
              <View key={line.finish} style={styles.finishRow}>
                <Text style={[styles.nameCell, styles.indent]}>{FINISH_LABELS[line.finish]}</Text>
                <Text style={styles.numCell}>{quantity(line.jobs)}</Text>
                <Text style={styles.numCell}>{quantity(line.pools)}</Text>
                <Text style={styles.rateCell}>{formatRates(line.rates)}</Text>
                <Text style={styles.numCell}>{money(line.poolPay)}</Text>
                <Text style={styles.numCell}>{money(line.travel)}</Text>
                <Text style={styles.numCell}>{money(line.extras)}</Text>
                <Text style={styles.numCell}>{money(line.pay)}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

//payee lists can run past a page, so unlike an operations section this one is allowed to break
const PayrollSection = ({ section }) => {
  const { totals, employees, contractors } = section;

  return (
    <View style={styles.section}>
      <View wrap={false}>
        <Text style={styles.heading}>{section.heading}</Text>
        <Text style={styles.subheading}>{section.subheading}</Text>
        <FigureRow label='Employee pay' value={money(totals.employeePay)} bold />
        <FigureRow label='Contractor pay' value={money(totals.contractorPay)} bold />
      </View>

      {employees &&
        (employees.length ? (
          <View style={{ marginTop: 10 }}>
            <PayeeTable payees={employees} showPoolColumns />
          </View>
        ) : (
          <Text style={styles.note}>No employees were paid in this period.</Text>
        ))}

      {contractors?.length > 0 && (
        <View>
          <Text style={styles.subtableHeading}>Contractors</Text>
          <PayeeTable payees={contractors} />
        </View>
      )}

      <Text style={styles.note}>{coverageText(section.coverage)}</Text>
    </View>
  );
};

//Rendered from the stored record alone, like the on-screen view, so a PDF of an old run carries the
//figures that run produced.
const ReportPDF = ({ record, title, period }) => {
  const sections = getSections(record.result, period);
  const isPayroll = record.type === 'PAYROLL';
  const Section = isPayroll ? PayrollSection : OperationsSection;
  const notes = isPayroll ? PAYROLL_NOTES : OPERATIONS_NOTES;

  return (
    <Document title={`${title} · ${period.label}`} author='US Gunite'>
      <Page size='LETTER' style={styles.page}>
        <Text style={styles.title}>
          {title} · {period.label}
        </Text>
        <Text style={styles.muted}>{period.rangeLabel}</Text>
        <Text style={styles.muted}>
          Run {formatRunAt(record.createdAt)}
          {record.requestedBy?.name ? ` by ${record.requestedBy.name}` : ''}. {STALENESS_NOTE}
        </Text>
        {getFilterLines(record.result.filter).map(line => (
          <Text key={line} style={[styles.muted, styles.bold]}>
            {line}
          </Text>
        ))}

        {sections.map(section => (
          <Section key={section.key} section={section} />
        ))}

        <View style={styles.notes} wrap={false}>
          {notes.map(note => (
            <Text key={note} style={[styles.note, { marginTop: 4 }]}>
              {note}
            </Text>
          ))}
        </View>

        <View style={styles.footer} fixed>
          <Text>
            {title} · {period.label}
          </Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default ReportPDF;
